import React, { useEffect, useMemo, useRef, useState } from "react";
import Question from "../question/question";
import "./questionWizard.scss";
import { IQuestion } from "../../../interfaces/IQuestion";
import Button from "../../atoms/button/button";
import ProgressCircle from "../../atoms/progress-circle/ProgressCircle";

interface mentoriaQuestionWizard {
	questions: Array<IQuestion>;
	formData: any;
	setData: any;
	onMovePreviousFallback: any;
	onMoveAfterFallback: any
	setHeaderStep?: any;
	parentRef: any;
	counter?: any;
	customProgressBar?: (progress:number) => React.ReactNode;
}

const QuestionWizard = (props: mentoriaQuestionWizard) => {
	
	const { counter = 0 } = props

	const {
		questions,
		formData,
		setData,
		onMoveAfterFallback,
		onMovePreviousFallback,
		setHeaderStep = () => { },
		parentRef,
	} = props;

	const [questionCounter, setQuestionCounter] = useState(counter);
	const [shouldDisplay, setShouldDisplay] = useState(false);
	const [error, setError] = useState({ message: "" });
	const [currentQuestion, setCurrentQuestion] = useState(questions[questionCounter]);
	const [moveBack, setMoveBack] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const headerRef: any = useRef(null);
	const questionValue = formData[currentQuestion.id];

	const progress = useMemo(
		() => Math.ceil(((questionCounter + 1) * 100) / questions.length),
		[questionCounter]
	);
	
	useEffect(() => {
		setShouldDisplay(false);

		if (questionCounter >= questions.length) {
			props.onMoveAfterFallback();
			return;
		}

		// Verificar si la pregunta tiene prerequisitos y si cumplen para poderla mostrar
		//TODO: revisar si se puede refactorizar
		if (Array.isArray(questions[questionCounter].preRequisiteQuestions) && questions[questionCounter].preRequisiteQuestions.length > 0) {
			
			const found = questions.find((question) => {
				if (questions[questionCounter].preRequisiteQuestions?.some(preReqQuestion => preReqQuestion.idQuestion === question.id)) {
					const matchedPreReqQuestion = questions[questionCounter].preRequisiteQuestions.find(preReqQuestion => preReqQuestion.idQuestion === question.id);
					if (
					  matchedPreReqQuestion &&
					  matchedPreReqQuestion.options.some(option => {
					    const questionData = formData[question.id];
					    if (Array.isArray(questionData)) {
					      return questionData.some(optionData => optionData.id === option.id);
					    } else {
					      return questionData?.id === option.id;
					    }
					  })
					) {
					  return question;
					}
				  }
				  return undefined;
			});
			if (!found) {
				if (moveBack) {
					setQuestionCounter(questionCounter - 1);
				} else {
					setQuestionCounter(questionCounter + 1);
				}
			}
		}
		setCurrentQuestion(questions[questionCounter]);
		setShouldDisplay(true);

	}, [questionCounter]);

	useEffect(() => {
		if (currentQuestion.required) {
			if (formData[currentQuestion.id] === 0) {
				setIsDisabled(false);
			} else {
				setIsDisabled((formData[currentQuestion.id] || currentQuestion.openResponse) ? false : true);
			}
		} else {
			setIsDisabled(false);
		}
	}, [questionValue, currentQuestion])


	const movePreviousQuestion = () => {
		setError({ message: "" });
		setMoveBack(true);
		if (questionCounter - 1 < 0) {
			onMovePreviousFallback();
		} else {
			setQuestionCounter(questionCounter - 1);
		}
	};

	const moveNextQuestion = () => {
		setError({ message: "" });
		setMoveBack(false);

		if (questions[questionCounter].required) {
			if ((!formData[questions[questionCounter].id] && formData[questions[questionCounter].id] !== 0) ||
				Array.isArray(formData[questions[questionCounter].id] && !formData[questions[questionCounter].id].length)) {
				setError({ message: "Debes responder antes de continuar" });
				return;
			}
		}
		if (questionCounter + 1 >= questions.length) {
			onMoveAfterFallback();
			return;
		}
		setQuestionCounter(questionCounter + 1);

	};

	useEffect(() => {
		setHeaderStep(questions[questionCounter].purpose);
		if (parentRef && parentRef.current) {
			const y = parentRef.current.getBoundingClientRect().top + window.pageYOffset - 70;
			window.scrollTo({
				top: y
			});
		}
	}, [questionCounter])

	return (
		<div className="o-mentoriaQuestionWizard-container">
			{props.customProgressBar && (
				<div className="o-mentoriaQuestionWizard-container__progress-circle">
					<div style={{ position: "fixed", right: 30, bottom: 100, zIndex: 10 }}>
						{props.customProgressBar(progress)}
					</div>
				</div>
			)}
			<div className="o-mentoriaQuestionWizard-container__back-button" onClick={movePreviousQuestion}>
				<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16">
					<path fillRule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5z" />
				</svg>
			</div>
		
			{shouldDisplay && <div id="i">
				<div ref={headerRef}>
					{currentQuestion &&
						<Question
							question={currentQuestion}
							value={formData[currentQuestion.id]}
							movePreviousQuestion={movePreviousQuestion}
							moveNextQuestion={moveNextQuestion}
							setData={setData}
							formData={formData}
							error={error}
							parentId={currentQuestion.id}
							onCustomChange={currentQuestion.onCustomChange}
						/>
					}
				</div>
				{(currentQuestion && currentQuestion.help && currentQuestion.type.id !== 7 && currentQuestion.type.id !== 15) &&
					<div className="o-mentoriaQuestionWizard-container__tooltip-container">
						<div className="o-mentoriaQuestionWizard-container__tooltip">
							<p>{currentQuestion.help}</p>
						</div>
					</div>

				}
				{error.message && <p className="o-mentoriaQuestionWizard-container__error-message">{error.message}</p>}
				<div className="o-mentoriaQuestionWizard-container__buttons-container">
					{questionCounter > 0 &&
						<Button
							type="button"
							className="o-mentoriaQuestionWizard-container__buttons-container__button"
							text="Volver"
							onClick={movePreviousQuestion}
							gtmClass="primary"
						/>
					}
					<Button
						type="button"
						gtmClass="secondary"
						className="o-mentoriaQuestionWizard-container__buttons-container__button"
						text={/*(currentQuestion && currentQuestion.next && currentQuestion.next.value) ? "Continuar" : "Finalizar"*/ 'Continuar'}
						onClick={moveNextQuestion}
						disabled={isDisabled}
					/>
				</div>
			</div>}
		</div>
	);
};

export default QuestionWizard;
